import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useAssignmentsList, ASSIGNMENTS_DEFAULT_PAGE_SIZE } from "./useAssignmentsList.js";
import { getAssignment, createAssignment, cancelAssignment } from "./assignmentsApi.js";
import { listMatchingQueueJobs, listMatchingQueueCandidates } from "../matchingQueue/matchingQueueApi.js";
import "./assignments.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Thailand Jobs", "Assignments"];
const DRAWER_TITLE_ID = "asg-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

const ASSIGNMENT_STATUS_OPTIONS = ["Active", "Cancelled", "Completed"];

const HERO_COPY_TEXT = "Assignment คือผลการจับคู่แรงงานกับงานแล้ว แต่ยังไม่ใช่ Tracking / Device Binding";
const TOP_WARNING_TITLE = "ยังไม่เริ่มติดตามตำแหน่งและยังไม่ได้ผูกเครื่องของแรงงาน";
const TOP_WARNING_BODY =
  "หน้านี้จัดการเฉพาะสถานะการมอบหมายแรงงาน (Assignment) เท่านั้น ไม่มีการติดตามอุปกรณ์ ไม่มีการผูกอุปกรณ์ และไม่มี geofence ในหน้าจอนี้";
const CREATE_ASSIGNMENT_WARNING = "ระบบจะสร้าง Assignment เท่านั้น ยังไม่เริ่ม Tracking หรือผูกอุปกรณ์";
const CANCEL_ASSIGNMENT_WARNING = "การยกเลิกเป็นการเปลี่ยนสถานะ ไม่ลบข้อมูล และต้องมี audit log";
const FOOTNOTE_TEXT =
  "Assignment สร้างได้จากปุ่ม \"+ สร้าง Assignment\" ในหน้านี้เท่านั้น — งานต้องอยู่ในสถานะ ReadyForMatching หรือ PartiallyFilled และผู้สมัครต้องอยู่ในสถานะ Ready For Match ก่อนจึงจะสร้าง Assignment ได้ การยกเลิก Assignment เป็นการเปลี่ยนสถานะเท่านั้น ไม่ลบข้อมูล และมีการบันทึก audit log ทุกครั้ง";

const ASSIGNMENT_STATUS_BADGE_VARIANT = { Active: "success", Cancelled: "danger", Completed: "info" };

function assignmentStatusBadgeVariant(status) {
  return ASSIGNMENT_STATUS_BADGE_VARIANT[status] ?? "neutral";
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงรายการ Assignment นี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายละเอียด Assignment นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบ Assignment นี้ อาจถูกย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

function describeCreateError(error) {
  if (error?.validation) {
    return "กรุณาเลือกงานและผู้สมัครให้ครบถ้วน";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์สร้าง Assignment สำหรับงานหรือผู้สมัครรายนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบงานหรือผู้สมัครที่เลือก กรุณาลองใหม่อีกครั้ง";
  }
  if (error?.status === 409) {
    // ApiClientError.message is always the generic "Request failed with
    // status 409" (apiClient.js never parses/attaches the response body's
    // own message) - never trust it as user-facing copy here, same
    // precedent ThailandJobsJobsPage.describeMarkReadyError already sets.
    return "งานหรือผู้สมัครนี้ยังไม่ผ่านเงื่อนไขสำหรับสร้าง Assignment (เช่น งานเต็มแล้ว ผู้สมัครยังไม่พร้อม หรือผู้สมัครมี Assignment ที่ Active อยู่แล้ว) กรุณาตรวจสอบแล้วลองใหม่อีกครั้ง";
  }
  if (error?.status === 400) {
    return "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  return "ไม่สามารถสร้าง Assignment ได้ กรุณาลองใหม่อีกครั้ง";
}

function describeCancelError(error) {
  if (error?.validation) {
    return "กรุณาระบุเหตุผลการยกเลิก";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ยกเลิก Assignment นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบ Assignment นี้ อาจถูกย้ายแล้ว";
  }
  if (error?.status === 409) {
    return "สถานะของ Assignment นี้เปลี่ยนไปแล้ว กรุณารีเฟรชแล้วลองใหม่อีกครั้ง";
  }
  if (error?.status === 400) {
    return "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  return "ไม่สามารถยกเลิก Assignment ได้ กรุณาลองใหม่อีกครั้ง";
}

/** Two-letter monogram from a worker's name - never a fabricated icon/logo. */
function monogram(name) {
  const trimmed = (name ?? "").trim();
  if (!trimmed) {
    return "-";
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

function formatDateTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

function uniqueAgents(items) {
  const seen = new Map();
  for (const item of items) {
    if (item.agentId && !seen.has(item.agentId)) {
      seen.set(item.agentId, { id: item.agentId, code: item.agentCode, name: item.agentName });
    }
  }
  return [...seen.values()];
}

function uniqueEmployers(items) {
  const seen = new Map();
  for (const item of items) {
    if (item.employerId && !seen.has(item.employerId)) {
      seen.set(item.employerId, { id: item.employerId, name: item.employerName });
    }
  }
  return [...seen.values()];
}

function uniqueJobs(items) {
  const seen = new Map();
  for (const item of items) {
    if (item.jobId && !seen.has(item.jobId)) {
      seen.set(item.jobId, { id: item.jobId, code: item.jobCode, title: item.jobTitle });
    }
  }
  return [...seen.values()];
}

function uniqueWorkers(items) {
  const seen = new Map();
  for (const item of items) {
    if (item.workerId && !seen.has(item.workerId)) {
      seen.set(item.workerId, { id: item.workerId, code: item.workerCode, name: item.fullName });
    }
  }
  return [...seen.values()];
}

/**
 * /backoffice/thailand-jobs/assignments - the Worker Assignments screen
 * (Prompt 109B, built from the W52 reference). Read-heavy list/detail over
 * the Prompt 108 Assignment API, plus a real Create Assignment and Cancel
 * Assignment flow. Create Assignment is the only place in this whole
 * frontend that ever collects a workerId as a write - it never appears on
 * any job create/edit form. This screen never calls a tracking/device/
 * geofence/Invite-Card API (none of those exist anywhere in this codebase),
 * and canBindDevice/canStartTracking are always rendered as blocked,
 * matching AssignmentNextActionsDto's own always-false contract.
 */
export default function AssignmentsPage({ embedded = false }) {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [employerFilter, setEmployerFilter] = useState("");
  const [jobFilter, setJobFilter] = useState("");
  const [workerFilter, setWorkerFilter] = useState("");
  const [superAgentOrgInput, setSuperAgentOrgInput] = useState("");
  const [plcOrgInput, setPlcOrgInput] = useState("");
  const [page, setPage] = useState(1);

  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [drawerView, setDrawerView] = useState(null); // null | "detail" | "create" | "cancel"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  const [createForm, setCreateForm] = useState({ jobId: "", workerId: "", expectedStartDate: "", assignmentNote: "" });
  const [createJobOptions, setCreateJobOptions] = useState({ status: "idle", data: [] });
  const [createWorkerOptions, setCreateWorkerOptions] = useState({ status: "idle", data: [] });
  const [createSubmit, setCreateSubmit] = useState({ status: "idle", error: null });

  const [cancelReason, setCancelReason] = useState("");
  const [cancelSubmit, setCancelSubmit] = useState({ status: "idle", error: null });

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, assignmentStatusFilter, agentFilter, employerFilter, jobFilter, workerFilter, superAgentOrgInput, plcOrgInput]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: ASSIGNMENTS_DEFAULT_PAGE_SIZE,
      search,
      assignmentStatus: assignmentStatusFilter,
      superAgentOrganizationId: superAgentOrgInput,
      plcOrganizationId: plcOrgInput,
      agentId: agentFilter,
      employerId: employerFilter,
      jobId: jobFilter,
      workerId: workerFilter,
    }),
    [page, search, assignmentStatusFilter, superAgentOrgInput, plcOrgInput, agentFilter, employerFilter, jobFilter, workerFilter]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useAssignmentsList(filters);

  // Detail is fetched whenever selectedAssignmentId changes (row/card
  // opened, or a create-assignment success moves selection to the new
  // assignment) - it stays cached across the cancel sub-view for the same
  // assignment, and only refetches for the same id when a cancel succeeds
  // (detailReloadToken).
  useEffect(() => {
    if (!selectedAssignmentId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedAssignmentId ? previous.data : null,
      error: null,
      forId: selectedAssignmentId,
    }));

    getAssignment(selectedAssignmentId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedAssignmentId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedAssignmentId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedAssignmentId
  }, [selectedAssignmentId, detailReloadToken]);

  function openAssignmentDetail(assignmentId) {
    setSelectedAssignmentId(assignmentId);
    setDrawerView("detail");
  }

  function openCreateDrawer() {
    setCreateForm({ jobId: "", workerId: "", expectedStartDate: "", assignmentNote: "" });
    setCreateWorkerOptions({ status: "idle", data: [] });
    setCreateSubmit({ status: "idle", error: null });
    if (createJobOptions.status === "idle") {
      setCreateJobOptions({ status: "loading", data: [] });
      listMatchingQueueJobs({ page: 1, pageSize: 100 })
        .then((data) => setCreateJobOptions({ status: "ready", data: data.items ?? [] }))
        .catch(() => setCreateJobOptions({ status: "error", data: [] }));
    }
    setDrawerView("create");
  }

  function handleCreateJobChange(jobId) {
    setCreateForm((previous) => ({ ...previous, jobId, workerId: "" }));
    if (!jobId) {
      setCreateWorkerOptions({ status: "idle", data: [] });
      return;
    }
    setCreateWorkerOptions({ status: "loading", data: [] });
    listMatchingQueueCandidates(jobId, { page: 1, pageSize: 100 })
      .then((data) => setCreateWorkerOptions({ status: "ready", data: data.items ?? [] }))
      .catch(() => setCreateWorkerOptions({ status: "error", data: [] }));
  }

  function openCancelDrawer() {
    setCancelReason("");
    setCancelSubmit({ status: "idle", error: null });
    setDrawerView("cancel");
  }

  function backToDetail() {
    setDrawerView("detail");
  }

  function closeDrawer() {
    setDrawerView(null);
    setSelectedAssignmentId(null);
  }

  function handleRefresh() {
    refetchList();
    if (selectedAssignmentId) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  async function handleCreateSubmit(event) {
    event.preventDefault();
    if (!createForm.jobId || !createForm.workerId) {
      setCreateSubmit({ status: "error", error: { validation: true } });
      return;
    }
    setCreateSubmit({ status: "submitting", error: null });
    try {
      const created = await createAssignment({
        jobId: createForm.jobId,
        workerId: createForm.workerId,
        assignmentNote: createForm.assignmentNote.trim() || null,
        expectedStartDate: createForm.expectedStartDate.trim() || null,
      });
      setCreateSubmit({ status: "success", error: null });
      refetchList();
      if (created?.assignmentId) {
        setSelectedAssignmentId(created.assignmentId);
        setDrawerView("detail");
      } else {
        closeDrawer();
      }
    } catch (error) {
      setCreateSubmit({ status: "error", error });
    }
  }

  async function handleCancelSubmit(event) {
    event.preventDefault();
    if (!cancelReason.trim()) {
      setCancelSubmit({ status: "error", error: { validation: true } });
      return;
    }
    if (!selectedAssignmentId) {
      return;
    }
    setCancelSubmit({ status: "submitting", error: null });
    try {
      await cancelAssignment(selectedAssignmentId, { cancelReason: cancelReason.trim() });
      setCancelSubmit({ status: "success", error: null });
      refetchList();
      setDetailReloadToken((token) => token + 1);
      setDrawerView("detail");
    } catch (error) {
      setCancelSubmit({ status: "error", error });
    }
  }

  const items = listData?.items ?? [];
  const summary = listData?.summary ?? null;
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / ASSIGNMENTS_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(
    search || assignmentStatusFilter || agentFilter || employerFilter || jobFilter || workerFilter || superAgentOrgInput || plcOrgInput
  );

  const agentOptions = useMemo(() => uniqueAgents(items), [items]);
  const employerOptions = useMemo(() => uniqueEmployers(items), [items]);
  const jobOptions = useMemo(() => uniqueJobs(items), [items]);
  const workerOptions = useMemo(() => uniqueWorkers(items), [items]);

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  // Rendered inside the Matching Workspace as its Assignments tab, the page
  // supplies only its body - the workspace already provides the app shell.
  const wrap = (node) => (embedded ? node : <AppShell crumbs={CRUMBS}>{node}</AppShell>);

  if (isInitialLoading) {
    return wrap(
        <PageSkeleton variant="table" label="กำลังโหลดรายการ Assignment..." />
    );
  }

  if (isInitialError) {
    return wrap(
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายการ Assignment ได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
          <button type="button" className="wx-button wx-button--primary" onClick={handleRefresh}>
            ลองใหม่อีกครั้ง
          </button>
        </div>
    );
  }

  return wrap(
    <>
      <div className="wx-asg-page">
        <div className="wx-asg-toolbar">
          <label className="wx-asg-search">
            <span className="wx-visually-hidden">ค้นหารหัส Assignment ผู้สมัคร งาน หรือนายจ้าง</span>
            <input
              type="search"
              placeholder="ค้นหารหัส Assignment ผู้สมัคร งาน หรือนายจ้าง"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="wx-asg-filters">
            <label className="wx-asg-filter">
              <span>สถานะ</span>
              <select value={assignmentStatusFilter} onChange={(event) => setAssignmentStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {ASSIGNMENT_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-asg-filter">
              <span>Agent</span>
              <select value={agentFilter} onChange={(event) => setAgentFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {agentOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.code} · {option.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-asg-filter">
              <span>นายจ้าง</span>
              <select value={employerFilter} onChange={(event) => setEmployerFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {employerOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-asg-filter">
              <span>งาน</span>
              <select value={jobFilter} onChange={(event) => setJobFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {jobOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.code} · {option.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-asg-filter">
              <span>ผู้สมัคร</span>
              <select value={workerFilter} onChange={(event) => setWorkerFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {workerOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.code} · {option.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-asg-filter" title="ตัวกรองนี้ใช้งานได้จริง แต่ยังไม่มีรายชื่อ Super Agent ให้เลือก จึงต้องกรอกรหัสเอง">
              <span>รหัส Super Agent Org</span>
              <input type="text" placeholder="ไม่บังคับ" value={superAgentOrgInput} onChange={(event) => setSuperAgentOrgInput(event.target.value)} />
            </label>

            <label className="wx-asg-filter" title="ตัวกรองนี้ใช้งานได้จริง แต่ยังไม่มีรายชื่อ PLC ให้เลือก จึงต้องกรอกรหัสเอง">
              <span>รหัส PLC Org</span>
              <input type="text" placeholder="ไม่บังคับ" value={plcOrgInput} onChange={(event) => setPlcOrgInput(event.target.value)} />
            </label>
          </div>

          <div className="wx-asg-toolbar-actions">
            {/* wx-asg-refresh-button/wx-asg-create-button are also listed
                in DetailDrawer's selectableSelector below - without them,
                clicking either while the drawer is open is treated as an
                "outside click" as far as the drawer's own close-on-outside-
                click listener is concerned (see ../components/DetailDrawer.jsx). */}
            <button type="button" className="wx-button wx-button--ghost wx-asg-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
            <button type="button" className="wx-button wx-button--primary wx-asg-create-button" onClick={openCreateDrawer}>
              + สร้าง Assignment
            </button>
          </div>
        </div>

        <header className="wx-asg-head">
          <div className="wx-asg-title">
            <h1>
              Worker Assignments <span className="wx-asg-title-th">การมอบหมายแรงงาน</span>
            </h1>
            <p>{HERO_COPY_TEXT}</p>
          </div>
          {primaryRole && (
            <span className="wx-asg-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-asg-top-notice" role="status">
          <strong>{TOP_WARNING_TITLE}</strong>
          <span>{TOP_WARNING_BODY}</span>
        </div>

        <div className="wx-asg-metrics">
          <MetricCard label="Assignments ทั้งหมด" value={totalCount} note="จำนวน Assignment ทั้งหมดในขอบเขตที่มองเห็น (totalCount)" variant="blue" />
          <MetricCard label="Active" value={summary?.activeCount} note="กำลังดำเนินการอยู่ ยังไม่ยกเลิก (summary.activeCount)" variant="green" />
          <MetricCard label="Cancelled" value={summary?.cancelledCount} note="ถูกยกเลิกแล้ว ข้อมูลยังอยู่ครบ ไม่ถูกลบ (summary.cancelledCount)" />
        </div>

        <section className="wx-panel wx-asg-list-panel">
          <div className="wx-asg-list-head">
            <h2 className="wx-panel-title">รายการ Assignment</h2>
            <span className="wx-asg-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-asg-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบรายการที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มี Assignment ในขอบเขตที่มองเห็น"}</p>
          ) : (
            <>
              <div className="wx-asg-table-wrap">
                <table className="wx-asg-table">
                  <colgroup>
                    {/* 8 cols for 8 columns, summing to 100. The eight widths only reached 95%.
                        table-layout:fixed reads these in order and ignores
                        any extra, so a mismatched or short list sizes the
                        real columns off something that was never about
                        them. If a column is added or removed, change this
                        list too. */}
                    <col style={{ width: "15%" }} /><col style={{ width: "14%" }} /><col style={{ width: "15%" }} /><col style={{ width: "15%" }} />
                    <col style={{ width: "13%" }} /><col style={{ width: "10%" }} /><col style={{ width: "9%" }} /><col style={{ width: "9%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Assignment</th>
                      <th>ผู้สมัคร</th>
                      <th>งาน</th>
                      <th>นายจ้าง</th>
                      <th>Agent</th>
                      <th>คาดว่าจะเริ่มงาน</th>
                      <th>สถานะ</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.assignmentId} className="wx-asg-row" onClick={() => openAssignmentDetail(item.assignmentId)}>
                        <td>
                          <div className="wx-asg-job-cell">
                            <span className="wx-asg-avatar" aria-hidden="true">
                              {monogram(item.fullName)}
                            </span>
                            <div>
                              <span className="wx-asg-cell-strong">{item.assignmentCode}</span>
                              <span className="wx-asg-cell-sub">มอบหมาย {formatDateTh(item.assignedAtUtc) ?? "-"}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="wx-asg-cell-strong">{item.fullName ?? "-"}</span>
                          <span className="wx-asg-cell-sub">{item.workerCode ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-asg-cell-strong">{item.jobTitle ?? "-"}</span>
                          <span className="wx-asg-cell-sub">{item.jobCode ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-asg-cell-strong">{item.employerName ?? "-"}</span>
                          <span className="wx-asg-cell-sub">{item.province ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-asg-cell-strong">{item.agentCode ?? "-"}</span>
                          <span className="wx-asg-cell-sub">{item.agentName ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-asg-cell-strong">{formatDateTh(item.expectedStartDate) ?? "ไม่ระบุ"}</span>
                        </td>
                        <td>
                          <span className={`wx-badge wx-badge--${assignmentStatusBadgeVariant(item.assignmentStatus)}`}>{item.assignmentStatus}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="wx-asg-row-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              openAssignmentDetail(item.assignmentId);
                            }}
                          >
                            เปิดรายละเอียด
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-asg-cards">
                {items.map((item) => (
                  <AssignmentCard key={item.assignmentId} item={item} onOpen={() => openAssignmentDetail(item.assignmentId)} />
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>

        <p className="wx-asg-footnote">{FOOTNOTE_TEXT}</p>
      </div>

      <DetailDrawer
        open={drawerView !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-asg-row, .wx-asg-card, .wx-asg-refresh-button, .wx-asg-create-button"
      >
        {drawerView === "detail" && (
          <AssignmentDetailView titleId={DRAWER_TITLE_ID} state={detailState} onOpenCancel={openCancelDrawer} />
        )}
        {drawerView === "create" && (
          <CreateAssignmentView
            titleId={DRAWER_TITLE_ID}
            form={createForm}
            onChange={(field, value) => setCreateForm((previous) => ({ ...previous, [field]: value }))}
            onJobChange={handleCreateJobChange}
            jobOptions={createJobOptions}
            workerOptions={createWorkerOptions}
            onSubmit={handleCreateSubmit}
            onCancel={closeDrawer}
            submitState={createSubmit}
          />
        )}
        {drawerView === "cancel" && (
          <CancelAssignmentView
            titleId={DRAWER_TITLE_ID}
            assignment={detailState.data}
            cancelReason={cancelReason}
            onCancelReasonChange={setCancelReason}
            onSubmit={handleCancelSubmit}
            onBack={backToDetail}
            submitState={cancelSubmit}
          />
        )}
      </DetailDrawer>
    </>
  );
}

// The `reserved` branch of this card - a "สงวนไว้" value plus a pill naming
// the missing API - went with the last always-reserved tile that used it. A
// KPI tile exists to carry a number; keeping the branch alive would only
// invite the next one.
function MetricCard({ label, value, note, variant }) {
  const variantClass = variant ? ` wx-asg-metric--${variant}` : "";
  return (
    <div className={`wx-asg-metric${variantClass}`}>
      <span className="wx-asg-metric-label">{label}</span>
      <strong className="wx-asg-metric-value">{value ?? "-"}</strong>
      {note && <span className="wx-asg-metric-note">{note}</span>}
    </div>
  );
}

function AssignmentCard({ item, onOpen }) {
  return (
    <div className="wx-asg-card" onClick={onOpen}>
      <div className="wx-asg-card-top">
        <div className="wx-asg-job-cell">
          <span className="wx-asg-avatar" aria-hidden="true">
            {monogram(item.fullName)}
          </span>
          <div>
            <strong>{item.assignmentCode}</strong>
            <span>มอบหมาย {formatDateTh(item.assignedAtUtc) ?? "-"}</span>
          </div>
        </div>
        <span className={`wx-badge wx-badge--${assignmentStatusBadgeVariant(item.assignmentStatus)}`}>{item.assignmentStatus}</span>
      </div>
      <div className="wx-asg-card-grid">
        <div>
          <label>ผู้สมัคร</label>
          <strong>
            {item.fullName ?? "-"} · {item.workerCode ?? "-"}
          </strong>
        </div>
        <div>
          <label>งาน</label>
          <strong>
            {item.jobTitle ?? "-"} · {item.jobCode ?? "-"}
          </strong>
        </div>
        <div>
          <label>นายจ้าง</label>
          <strong>{item.employerName ?? "-"}</strong>
        </div>
        <div>
          <label>Agent</label>
          <strong>
            {item.agentCode ?? "-"} · {item.agentName ?? "-"}
          </strong>
        </div>
        <div>
          <label>คาดว่าจะเริ่มงาน</label>
          <strong className={item.expectedStartDate ? undefined : "wx-asg-reserved-text"}>{formatDateTh(item.expectedStartDate) ?? "ไม่ระบุ"}</strong>
        </div>
      </div>
      <button
        type="button"
        className="wx-asg-row-action wx-asg-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        เปิดรายละเอียด
      </button>
    </div>
  );
}

function AssignmentDetailView({ titleId, state, onOpenCancel }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-asg-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const nextActions = detail.nextActions ?? {};
  const isCancellable = detail.assignmentStatus === "Active";

  return (
    <div className="wx-asg-drawer">
      <span className="wx-asg-drawer-kicker">WORKER ASSIGNMENT · DETAIL</span>
      <h2 id={titleId} className="wx-asg-drawer-title">
        รายละเอียด Assignment
      </h2>
      <p className="wx-asg-drawer-sub">
        {detail.assignmentCode} · มอบหมายเมื่อ {formatDateTimeTh(detail.assignedAtUtc) ?? "-"}
      </p>
      <div className="wx-asg-drawer-status">
        <span className={`wx-badge wx-badge--${assignmentStatusBadgeVariant(detail.assignmentStatus)}`}>{detail.assignmentStatus}</span>
        <span className="wx-badge wx-badge--danger">{TOP_WARNING_TITLE}</span>
      </div>

      <section className="wx-asg-dsec">
        <div className="wx-asg-title-block">
          <span className="wx-asg-avatar wx-asg-avatar--lg" aria-hidden="true">
            {monogram(detail.fullName)}
          </span>
          <div className="wx-asg-title-block-text">
            <strong>{detail.fullName ?? "-"}</strong>
            <span>{detail.workerCode ?? "-"} · ผู้สมัคร (Worker)</span>
          </div>
        </div>
        <div className="wx-asg-kv-row">
          <span>สถานะ Assignment</span>
          <strong>{detail.assignmentStatus}</strong>
        </div>
        <div className="wx-asg-kv-row">
          <span>คาดว่าจะเริ่มงาน</span>
          <strong className={detail.expectedStartDate ? undefined : "wx-asg-reserved-text"}>{formatDateTh(detail.expectedStartDate) ?? "ไม่ระบุ"}</strong>
        </div>
        <div className="wx-asg-kv-row">
          <span>มอบหมายเมื่อ</span>
          <strong>{formatDateTimeTh(detail.assignedAtUtc) ?? "-"}</strong>
        </div>
        <div className="wx-asg-kv-row">
          <span>มอบหมายโดย</span>
          <strong>{detail.assignedByUserId ?? "ไม่ระบุ"}</strong>
        </div>
        <div className="wx-asg-kv-row">
          <span>หมายเหตุ</span>
          <strong>{detail.assignmentNote || "ไม่ระบุ"}</strong>
        </div>
      </section>

      <section className="wx-asg-dsec">
        <h3>งาน</h3>
        <div className="wx-asg-kv-row">
          <span>ชื่อตำแหน่งงาน</span>
          <strong>{detail.jobTitle ?? "-"}</strong>
        </div>
        <div className="wx-asg-kv-row">
          <span>รหัสงาน</span>
          <strong className="wx-asg-code">{detail.jobCode ?? "-"}</strong>
        </div>
      </section>

      <section className="wx-asg-dsec">
        <h3>นายจ้าง</h3>
        <div className="wx-asg-kv-row">
          <span>นายจ้าง</span>
          <strong>{detail.employerName ?? "-"}</strong>
        </div>
        <div className="wx-asg-kv-row">
          <span>จังหวัด</span>
          <strong>{detail.province ?? "ไม่ระบุ"}</strong>
        </div>
      </section>

      <section className="wx-asg-dsec">
        <h3>Agent</h3>
        <div className="wx-asg-kv-row">
          <span>Agent</span>
          <strong>
            {detail.agentCode ?? "-"} · {detail.agentName ?? "-"}
          </strong>
        </div>
      </section>

      {detail.assignmentStatus === "Cancelled" && (
        <section className="wx-asg-dsec">
          <h3>เหตุผลการยกเลิก</h3>
          <div className="wx-asg-notice wx-asg-notice--warn">{detail.cancelReason || "ไม่ระบุ"}</div>
        </section>
      )}

      <section className="wx-asg-dsec">
        <h3>ขั้นตอนถัดไป</h3>
        <div className="wx-asg-next-actions">
          <div className={`wx-asg-next-action-row${nextActions.canStartOnboarding ? " wx-asg-next-action-row--ok" : ""}`}>
            <span className={`wx-asg-check-dot${nextActions.canStartOnboarding ? " wx-asg-check-dot--ok" : ""}`} />
            เริ่มขั้นตอนปฐมนิเทศ (Onboarding) — ข้อมูลเชิงสถานะเท่านั้น ยังไม่มีระบบ Onboarding จริงในระบบวันนี้
          </div>
          {/* Two blocked rows reading "ผูกอุปกรณ์ (Bind Device) — สงวนไว้" and
              "เริ่มติดตามอุปกรณ์ (Start Tracking) — สงวนไว้" were here. Both
              named an internal capability and neither could ever be actioned;
              the sentence below already explains the state. */}
        </div>
        <p className="wx-asg-note-muted wx-asg-note-muted--left">{nextActions.trackingReservedReason || TOP_WARNING_TITLE}</p>
      </section>

      <div className="wx-asg-drawer-foot">
        {isCancellable ? (
          <Link className="wx-button wx-button--ghost" to={`/backoffice/thailand-jobs/assignments/${detail.assignmentId}/onboarding`}>
            เปิด Onboarding
          </Link>
        ) : (
          <button
            type="button"
            className="wx-button wx-button--ghost"
            disabled
            title="Onboarding ใช้ได้เฉพาะ Assignment ที่อยู่ในสถานะ Active เท่านั้น"
          >
            เปิด Onboarding
          </button>
        )}
        {isCancellable && (
          <button type="button" className="wx-button wx-button--danger-outline" onClick={onOpenCancel}>
            ยกเลิก Assignment
          </button>
        )}
      </div>
    </div>
  );
}

function CreateAssignmentView({ titleId, form, onChange, onJobChange, jobOptions, workerOptions, onSubmit, onCancel, submitState }) {
  return (
    <form className="wx-asg-drawer" onSubmit={onSubmit}>
      <span className="wx-asg-drawer-kicker">CREATE ASSIGNMENT</span>
      <h2 id={titleId} className="wx-asg-drawer-title">
        สร้าง Assignment ใหม่
      </h2>
      <p className="wx-asg-drawer-sub">เชื่อมผู้สมัครที่พร้อม Match เข้ากับงานที่พร้อม Matching หลังยืนยันจาก Matching Queue แล้ว</p>

      <div className="wx-asg-notice wx-asg-notice--warn">{CREATE_ASSIGNMENT_WARNING}</div>

      <div className="wx-asg-field">
        <label>
          งาน (Job) <b>*</b>
        </label>
        <select value={form.jobId} onChange={(event) => onJobChange(event.target.value)} disabled={jobOptions.status === "loading"}>
          <option value="">เลือกงาน</option>
          {jobOptions.data.map((job) => (
            <option key={job.jobId} value={job.jobId}>
              {job.jobCode} · {job.jobTitle} · เหลือ {job.remainingWorkerCount ?? 0} อัตรา
            </option>
          ))}
        </select>
      </div>

      <div className="wx-asg-field">
        <label>
          ผู้สมัคร (Worker) <b>*</b>
        </label>
        <select
          value={form.workerId}
          onChange={(event) => onChange("workerId", event.target.value)}
          disabled={!form.jobId || workerOptions.status === "loading"}
        >
          <option value="">{form.jobId ? "เลือกผู้สมัคร" : "เลือกงานก่อน"}</option>
          {workerOptions.data.map((candidate) => (
            <option key={candidate.workerId} value={candidate.workerId}>
              {candidate.workerCode} · {candidate.fullName} · {candidate.matchLevel} {candidate.matchScore}
            </option>
          ))}
        </select>
      </div>

      <div className="wx-asg-context-pill">
        ผู้สมัครและงานทั้งสองต้องอยู่ในขอบเขต (scope) ของผู้ใช้งาน และงานต้องมีอัตราว่างเหลืออยู่ ระบบฝั่งจริงจะตรวจสอบเงื่อนไขนี้อีกครั้งเสมอ ไม่ใช่การจับคู่อัตโนมัติ (auto-match)
      </div>

      <div className="wx-asg-field">
        <label>วันที่คาดว่าจะเริ่มงาน (Expected Start Date)</label>
        <input type="date" value={form.expectedStartDate} onChange={(event) => onChange("expectedStartDate", event.target.value)} />
      </div>

      <div className="wx-asg-field">
        <label>หมายเหตุ (Assignment Note)</label>
        <textarea
          placeholder="ไม่บังคับกรอก เช่น เงื่อนไขพิเศษ หรือข้อตกลงเพิ่มเติมกับนายจ้าง"
          value={form.assignmentNote}
          onChange={(event) => onChange("assignmentNote", event.target.value)}
        />
      </div>

      <p className="wx-asg-note-muted wx-asg-note-muted--left">
        แบบฟอร์มนี้เป็นจุดเดียวในระบบ Thailand Jobs ที่รับ workerId — หน้าจอสร้าง/แก้ไขงาน (Jobs) จะไม่รับ workerId ใด ๆ ทั้งสิ้น
      </p>

      {submitState.status === "error" && <p className="wx-asg-form-message">{describeCreateError(submitState.error)}</p>}

      <div className="wx-asg-drawer-foot">
        <button type="button" className="wx-button wx-button--ghost" onClick={onCancel}>
          ยกเลิก
        </button>
        <button type="submit" className="wx-button wx-button--primary" disabled={submitState.status === "submitting"}>
          {submitState.status === "submitting" ? "กำลังสร้าง..." : "สร้าง Assignment"}
        </button>
      </div>
    </form>
  );
}

function CancelAssignmentView({ titleId, assignment, cancelReason, onCancelReasonChange, onSubmit, onBack, submitState }) {
  return (
    <form className="wx-asg-drawer" onSubmit={onSubmit}>
      <span className="wx-asg-drawer-kicker">CANCEL ASSIGNMENT</span>
      <h2 id={titleId} className="wx-asg-drawer-title">
        ยกเลิก Assignment
      </h2>
      <p className="wx-asg-drawer-sub">ยืนยันการยกเลิก Assignment นี้ — ต้องระบุเหตุผลทุกครั้ง</p>

      {assignment && (
        <div className="wx-asg-context-pill">
          Assignment <strong>{assignment.assignmentCode}</strong> · ผู้สมัคร <strong>{assignment.fullName}</strong> · งาน{" "}
          <strong>
            {assignment.jobTitle} ({assignment.jobCode})
          </strong>
        </div>
      )}

      <div className="wx-asg-notice wx-asg-notice--warn">{CANCEL_ASSIGNMENT_WARNING}</div>

      <div className="wx-asg-field">
        <label>
          เหตุผลการยกเลิก (Cancel Reason) <b>*</b>
        </label>
        <textarea
          placeholder="ระบุเหตุผลการยกเลิก Assignment นี้ เช่น ผู้สมัครไม่สะดวกเริ่มงาน หรือมีการเปลี่ยนแปลงความต้องการของนายจ้าง"
          value={cancelReason}
          onChange={(event) => onCancelReasonChange(event.target.value)}
        />
      </div>

      <p className="wx-asg-note-muted wx-asg-note-muted--left">
        เมื่อยกเลิกแล้ว งานจะกลับไปมีอัตราว่างเพิ่มขึ้น 1 อัตรา และผู้สมัครจะกลับสู่สถานะไม่มีนายจ้างปัจจุบัน สามารถถูกมอบหมายให้งานอื่นได้อีกครั้ง
      </p>

      {submitState.status === "error" && <p className="wx-asg-form-message">{describeCancelError(submitState.error)}</p>}

      <div className="wx-asg-drawer-foot">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack}>
          ย้อนกลับ
        </button>
        <button type="submit" className="wx-button wx-button--danger" disabled={submitState.status === "submitting"}>
          {submitState.status === "submitting" ? "กำลังยกเลิก..." : "ยืนยันยกเลิก Assignment"}
        </button>
      </div>
    </form>
  );
}
